"use client";

import { useId } from "react";
import { ChevronsUpDown } from "@/components/icons";
import { GroupedListItem } from "@/components/ui/grouped-list";

interface SelectOption {
  label: string;
  value: string;
}

export function PhoneSelectRow({
  label,
  value,
  options,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly SelectOption[];
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <GroupedListItem className="relative p-0">
      <label
        htmlFor={id}
        className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2.5"
      >
        <span className="shrink-0 text-foreground">{label}</span>
        <span className="relative flex min-w-0 items-center">
          <select
            id={id}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="min-w-0 max-w-48 appearance-none truncate border-0 bg-transparent bg-none py-1 pl-2 pr-6 text-right text-base text-muted-foreground focus-visible:outline-none focus-visible:ring-0"
          >
            <option value="">{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronsUpDown
            className="pointer-events-none absolute right-0 size-4 text-fg-subtle"
            aria-hidden="true"
          />
        </span>
      </label>
    </GroupedListItem>
  );
}

export function PhoneNumberRow({
  label,
  value,
  prefix,
  placeholder,
  min,
  onChange,
}: {
  label: string;
  value: number | null;
  prefix?: string;
  placeholder: string;
  min?: number;
  onChange: (value: number | null) => void;
}) {
  const id = useId();
  return (
    <GroupedListItem className="p-0">
      <label
        htmlFor={id}
        className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2.5"
      >
        <span className="shrink-0 text-foreground">{label}</span>
        <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
          {prefix ? <span aria-hidden="true">{prefix}</span> : null}
          <input
            id={id}
            type="number"
            inputMode="numeric"
            min={min}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(event) =>
              onChange(event.target.value ? Number(event.target.value) : null)
            }
            className="w-24 border-0 bg-transparent p-0 py-1 text-right text-base tabular-nums text-foreground placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-0"
          />
        </span>
      </label>
    </GroupedListItem>
  );
}
